import { Box, SliderMarkLabel, SliderProps, Typography } from "@mui/material";
import { ReactNode } from "react";
import { Control, FieldPath, FieldValues } from "react-hook-form";
import { SliderElement } from "react-hook-form-mui";

import { RangeSliderContent } from "./RangeSliderContent";

type GaiaSliderElementProps<T extends FieldValues> = Omit<SliderProps, "name"> & {
    label: string;
    name?: FieldPath<T>;
    minName?: FieldPath<T>;
    maxName?: FieldPath<T>;
    inputElement?: ({ min, max, step }: { min?: number; max?: number; step?: number | null }) => ReactNode;
    control: Control<T>;
};

export default function GaiaSliderElement<T extends FieldValues>({
    label,
    name,
    minName,
    maxName,
    inputElement,
    control,
    sx,
    disabled,
    min,
    max,
    step,
    ...sliderElementProps
}: GaiaSliderElementProps<T>) {
    const isRangeSlider = !!(minName && maxName);

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "116px 1fr 116px",
                columnGap: 5,
                alignItems: "center",
                ...sx,
            }}
        >
            <Typography variant="body2">{label}</Typography>

            <Box>
                {isRangeSlider ? (
                    <RangeSliderContent
                        control={control}
                        minName={minName}
                        maxName={maxName}
                        disabled={disabled}
                        min={min}
                        max={max}
                        step={step}
                        {...sliderElementProps}
                    />
                ) : (
                    name && (
                        <SliderElement
                            control={control}
                            name={name}
                            valueLabelDisplay="auto"
                            disabled={disabled}
                            min={min}
                            max={max}
                            step={step}
                            marks={sliderElementProps.marks}
                            valueLabelFormat={sliderElementProps.valueLabelFormat}
                            slots={{
                                markLabel: component => {
                                    return (
                                        <SliderMarkLabel
                                            {...component}
                                            style={{
                                                ...component.style,
                                                textWrap: "wrap",
                                                maxWidth: "40px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <Typography variant="caption">{component.children}</Typography>
                                        </SliderMarkLabel>
                                    );
                                },
                            }}
                        />
                    )
                )}
            </Box>

            {inputElement?.({ min, max, step })}
        </Box>
    );
}
